import { useState, useEffect } from 'react';
import { useParams } from 'react-router-dom';
import api from './api';
import { toast } from 'react-hot-toast';
import Modal from 'react-modal';
import { 
  FaClock, FaEye, FaEyeSlash, FaWallet, FaMoneyBillWave, 
  FaChartLine, FaCoins, FaExchangeAlt, FaArrowUp, 
  FaCheck, FaTimes, FaBan, FaEdit 
} from 'react-icons/fa';
import Nav from './nav.js';

const customStyles = {
  content: {
    top: '50%',
    left: '50%',
    right: 'auto',
    bottom: 'auto',
    marginRight: '-50%',
    transform: 'translate(-50%, -50%)',
    width: '90%',
    maxWidth: '500px',
    border: 'none',
    borderRadius: '16px',
    padding: '0',
    background: 'transparent',
    boxShadow: '0 25px 50px -12px rgba(0, 0, 0, 0.5)',
    maxHeight: '90vh',
    overflow: 'auto'
  },
  overlay: {
    backgroundColor: 'rgba(0, 0, 0, 0.75)',
    backdropFilter: 'blur(8px)',
    zIndex: 1000
  }
};

const statusConfig = {
  pending: { color: 'text-yellow-500', bg: 'bg-yellow-500/10', border: 'border-yellow-500/20' },
  completed: { color: 'text-green-500', bg: 'bg-green-500/10', border: 'border-green-500/20' },
  failed: { color: 'text-red-500', bg: 'bg-red-500/10', border: 'border-red-500/20' },
  cancelled: { color: 'text-gray-500', bg: 'bg-gray-500/10', border: 'border-gray-500/20' },
  processing: { color: 'text-blue-500', bg: 'bg-blue-500/10', border: 'border-blue-500/20' }
};

const statusIcons = {
  pending: <FaClock className="h-4 w-4 text-yellow-500" />,
  processing: <FaArrowUp className="h-4 w-4 text-blue-500" />,
  completed: <FaCheck className="h-4 w-4 text-green-500" />,
  failed: <FaTimes className="h-4 w-4 text-red-500" />,
  cancelled: <FaBan className="h-4 w-4 text-gray-500" />,
};

export default function UserDetail() {
  const { id } = useParams();
  const [user, setUser] = useState(null);
  const [wallet, setWallet] = useState(null);
  const [deposits, setDeposits] = useState([]);
  const [withdrawals, setWithdrawals] = useState([]);
  const [loading, setLoading] = useState(true);

  const [showEditProfileModal, setShowEditProfileModal] = useState(false);
  const [showAdjustModal, setShowAdjustModal] = useState(false);
  const [showPasswordModal, setShowPasswordModal] = useState(false);
  const [isStatusModalOpen, setIsStatusModalOpen] = useState(false);
  const [selectedTransaction, setSelectedTransaction] = useState(null);
  const [newStatus, setNewStatus] = useState('pending');
  const [newKycStatus, setKycNewStatus] = useState('pending');
  const [KycAmount, setKycAmount] = useState('');
  
  // Form states
  const [totalBalance, setTotalBalance] = useState('');
  const [availableBalance, setAvailableBalance] = useState('');
  const [totalProfit, setTotalProfit] = useState('');
  const [totalDeposits, setTotalDeposits] = useState('');
  const [totalWithdrawals, setTotalWithdrawals] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);

  // Loading states
  const [isEditingProfile, setIsEditingProfile] = useState(false);
  const [isAdjustingBalance, setIsAdjustingBalance] = useState(false);
  const [isUpdatingPassword, setIsUpdatingPassword] = useState(false);
  const [isUpdatingStatus, setIsUpdatingStatus] = useState(false);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const response = await api.get(`${process.env.REACT_APP_API_BASE_URL}/admin/users/${id}`);
        setUser(response.data.user);
        setWallet(response.data.wallet);
        setDeposits(response.data.deposits);
        setWithdrawals(response.data.withdrawals);
        
        // Set form values with current wallet data
        if (response.data.wallet) {
          setTotalBalance(response.data.wallet.totalBalance || '0');
          setAvailableBalance(response.data.wallet.availableBalance || '0');
          setTotalProfit(response.data.wallet.totalProfit || '0');
          setTotalDeposits(response.data.wallet.totalDeposits || '0');
          setTotalWithdrawals(response.data.wallet.totalWithdrawals || '0');
        }
      } catch (error) {
        toast.error('Failed to load user details');
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, [id]);

  const handleBalanceUpdate = async (data) => {
    setIsAdjustingBalance(true);
    try {
      const response = await api.put(
        `${process.env.REACT_APP_API_BASE_URL}/admin/users/${id}/wallet`,
        data
      );
      toast.success('Wallet updated successfully');
      setWallet(response.data.wallet);
      setShowAdjustModal(false);
    } catch (err) {
      console.log(err);
      toast.error('Update failed');
    } finally {
      setIsAdjustingBalance(false);
    }
  };

  const handlePasswordUpdate = async (e) => {
    e.preventDefault();
    setIsUpdatingPassword(true);
    try {
      await api.put(`${process.env.REACT_APP_API_BASE_URL}/admin/users/${id}/password`, {
        newPassword
      });
      toast.success('Password updated successfully');
      setShowPasswordModal(false);
      setNewPassword('');
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to update password');
    } finally {
      setIsUpdatingPassword(false);
    }
  };

  // Open status update modal
  const openStatusModal = (transaction, type) => {
    setSelectedTransaction({ ...transaction, type });
    setNewStatus(transaction.status);
    setIsStatusModalOpen(true);
  };

  // Handle transaction status update
  const handleStatusUpdate = async () => {
    if (!selectedTransaction) return;

    try {
      setIsUpdatingStatus(true);
      
      // Make API call to update transaction status
      await api.put(
        `${process.env.REACT_APP_API_BASE_URL}/admin/transactions/${selectedTransaction._id}/status`,
        {
          status: newStatus,
          note: "Status updated by admin"
        }
      );
      
      // Update local state based on transaction type
      if (selectedTransaction.type === 'deposit') {
        setDeposits(prevDeposits => 
          prevDeposits.map(deposit => 
            deposit._id === selectedTransaction._id 
              ? { ...deposit, status: newStatus }
              : deposit
          )
        );
      } else {
        setWithdrawals(prevWithdrawals => 
          prevWithdrawals.map(withdrawal => 
            withdrawal._id === selectedTransaction._id 
              ? { ...withdrawal, status: newStatus }
              : withdrawal
          )
        );
      }
      
      toast.success('Transaction status updated successfully');
      setIsStatusModalOpen(false);
      setSelectedTransaction(null);
    } catch (error) {
      console.error('Status update error:', error);
      toast.error(error.response?.data?.message || 'Failed to update transaction status');
    } finally {
      setIsUpdatingStatus(false);
    }
  };

  const handleProfileEdit = async (e) => {
    e.preventDefault();
    setIsEditingProfile(true);
    const formData = new FormData(e.target);
    const updates = {
      name: formData.get('name'),
      email: formData.get('email'),
      phone: formData.get('phone'),
      currency: formData.get('currency'),
    };
    
    try {
      await api.put(`${process.env.REACT_APP_API_BASE_URL}/admin/users/${id}`, updates);
      toast.success('Profile updated successfully');
      setShowEditProfileModal(false);
      const response = await api.get(`${process.env.REACT_APP_API_BASE_URL}/admin/users/${id}`);
      setUser(response.data.user);
    } catch (error) {
      toast.error(error.response?.data?.message || 'Failed to update profile');
    } finally {
      setIsEditingProfile(false);
    }
  };

  // Get valid status options based on transaction type
  const getValidStatuses = (type) => {
    if (type === 'deposit') {
      return ['pending', 'completed', 'failed', 'cancelled'];
    } else {
      return ['pending', 'processing', 'completed', 'failed', 'cancelled'];
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-900 to-black flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-16 w-16 border-t-2 border-b-2 border-blue-500 mx-auto mb-4"></div>
          <p className="text-gray-400 text-lg">Loading user details...</p>
        </div>
      </div>
    );
  }

  // Status Modal Component
  const StatusModal = () => (
    <Modal
      isOpen={isStatusModalOpen}
      style={customStyles}
      onRequestClose={() => setIsStatusModalOpen(false)}
      contentLabel="Update Status"
      ariaHideApp={false}
    >
      <div className="bg-gray-800 p-6 rounded-xl text-white border border-gray-700">
        <h3 className="text-xl font-bold mb-4 bg-gradient-to-r from-blue-400 to-purple-400 bg-clip-text text-transparent">
          Update Transaction Status
        </h3>

        {selectedTransaction && (
          <>
            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-300 mb-2">Transaction Type</label>
              <div className="text-white font-medium capitalize">
                {selectedTransaction.type} - ${selectedTransaction.amount}
              </div>
            </div>

            <div className="mb-4">
              <label className="block text-sm font-medium text-gray-300 mb-2">Current Status</label>
              <div className="flex items-center space-x-2">
                {statusIcons[selectedTransaction.status]}
                <span className="capitalize text-white">{selectedTransaction.status}</span>
              </div>
            </div>
            
            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-300 mb-2">New Status</label>
              <select
                value={newStatus}
                onChange={(e) => setNewStatus(e.target.value)}
                className="w-full bg-gray-700 rounded-lg px-3 py-2 text-white border border-gray-600 focus:border-blue-500 focus:ring-1 focus:ring-blue-500 transition-colors"
              >
                {getValidStatuses(selectedTransaction.type).map((status) => (
                  <option key={status} value={status} className="capitalize bg-gray-800">
                    {status}
                  </option>
                ))}
              </select>
            </div>

            <div className="flex justify-end space-x-3">
              <button
                onClick={() => setIsStatusModalOpen(false)}
                className="px-4 py-2 bg-gray-700 hover:bg-gray-600 rounded-lg transition-colors duration-200 font-medium"
              >
                Cancel
              </button>
              <button
                onClick={handleStatusUpdate}
                className={`px-4 py-2 bg-gradient-to-r from-blue-500 to-purple-600 hover:from-blue-600 hover:to-purple-700 rounded-lg transition-all duration-200 font-medium flex items-center ${
                  isUpdatingStatus ? 'opacity-50 cursor-not-allowed' : ''
                }`}
                disabled={isUpdatingStatus}
              >
                {isUpdatingStatus ? (
                  <>
                    <FaClock className="animate-spin mr-2" /> Updating...
                  </>
                ) : 'Update Status'}
              </button>
            </div>
          </>
        )}
      </div>
    </Modal>
  );

  // Transaction item component with status update functionality
  const TransactionItem = ({ transaction, type }) => (
    <div key={transaction._id} className="flex items-center justify-between p-3 bg-gray-700/30 rounded-lg hover:bg-gray-700/50 transition-colors duration-200 group">
      <div>
        <div className="text-white font-medium">${transaction.amount}</div>
        <div className="text-gray-400 text-sm">
          {type === 'deposit' ? transaction.currency : transaction.network}
        </div>
        {transaction.transactionHash && (
          <div className="text-gray-400 text-xs mt-1 truncate max-w-xs">
            Hash: {transaction.transactionHash}
          </div>
        )}
      </div>
      <div className="text-right">
        <div className="flex items-center space-x-2 justify-end mb-2">
          <div className={`px-2 py-1 rounded-full text-xs font-medium flex items-center space-x-1 ${
            statusConfig[transaction.status]?.bg || 'bg-gray-500/10'
          } ${statusConfig[transaction.status]?.color || 'text-gray-400'} border ${
            statusConfig[transaction.status]?.border || 'border-gray-500/20'
          }`}>
            {statusIcons[transaction.status]}
            <span className="capitalize">{transaction.status}</span>
          </div>
          <button
            onClick={() => openStatusModal(transaction, type)}
            className="opacity-0 group-hover:opacity-100 transition-opacity duration-200 p-1 text-gray-400 hover:text-blue-400"
            title="Update Status"
          >
            <FaEdit className="h-3 w-3" />
          </button>
        </div>
        <div className="text-gray-400 text-sm">
          {new Date(transaction.createdAt).toLocaleDateString()}
        </div>
      </div>
    </div>
  );

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-900 to-black text-white">
      {/* Header */}
      <div className="border-b border-gray-800 bg-gray-900/50 backdrop-blur-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-6">
            <div>
              <h1 className="text-3xl font-bold bg-gradient-to-r from-white to-gray-300 bg-clip-text text-transparent">
                User Management
              </h1>
              <p className="text-gray-400 mt-1">Manage user accounts and balances</p>
            </div>
            <div className="flex space-x-3">
              <button
                onClick={() => setShowEditProfileModal(true)}
                className="px-4 py-2 bg-gray-800 hover:bg-gray-700 rounded-lg transition-colors duration-200 font-medium border border-gray-700"
              >
                Edit Profile
              </button>
              <button
                onClick={() => setShowPasswordModal(true)}
                className="px-4 py-2 bg-blue-600 hover:bg-blue-700 rounded-lg transition-colors duration-200 font-medium"
              >
                Change Password
              </button>
              <button
                onClick={() => setShowAdjustModal(true)}
                className="px-4 py-2 bg-gradient-to-r from-blue-500 to-purple-600 hover:from-blue-600 hover:to-purple-700 rounded-lg transition-all duration-200 font-medium shadow-lg shadow-blue-500/25"
              >
                Adjust Balance
              </button>
            </div>
          </div>
        </div>
      </div>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* User Info Card */}
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-8">
          {/* User Profile */}
          <div className="bg-gray-800/50 backdrop-blur-sm rounded-2xl p-6 border border-gray-700/50">
            <div className="flex items-center space-x-4 mb-6">
              <div className="w-16 h-16 bg-gradient-to-r from-blue-500 to-purple-600 rounded-2xl flex items-center justify-center text-white text-2xl font-bold">
                {user?.name?.charAt(0).toUpperCase() || 'U'}
              </div>
              <div>
                <h2 className="text-xl font-bold text-white">{user?.name}</h2>
                <p className="text-gray-400">{user?.email}</p>
                <div className="flex items-center space-x-2 mt-1">
                  <div className={`px-2 py-1 rounded-full text-xs font-medium ${
                    user?.kycStatus === 'completed' 
                      ? 'bg-green-500/20 text-green-400 border border-green-500/30'
                      : 'bg-yellow-500/20 text-yellow-400 border border-yellow-500/30'
                  }`}>
                    KYC: {user?.kycStatus || 'pending'}
                  </div>
                </div>
              </div>
            </div>
            
            <div className="space-y-3">
              <div className="flex justify-between items-center">
                <span className="text-gray-400">Phone</span>
                <span className="text-white font-medium">{user?.phone || 'Not set'}</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-gray-400">Currency</span>
                <span className="text-white font-medium">{user?.currency || 'USD'}</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-gray-400">KYC Amount</span>
                <span className="text-white font-medium">${user?.kycAmount || '0'}</span>
              </div>
              <div className="flex justify-between items-center">
                <span className="text-gray-400">Member Since</span>
                <span className="text-white font-medium">
                  {new Date(user?.createdAt).toLocaleDateString()}
                </span>
              </div>
            </div>
          </div>

          {/* Wallet Overview */}
          <div className="lg:col-span-2 grid grid-cols-2 md:grid-cols-3 gap-4">
            <div className="bg-gradient-to-br from-blue-500/10 to-blue-600/10 rounded-2xl p-6 border border-blue-500/20">
              <div className="flex items-center justify-between mb-4">
                <FaWallet className="text-blue-400 text-xl" />
                <span className="text-blue-400 text-sm font-medium">Total Balance</span>
              </div>
              <div className="text-2xl font-bold text-white">${wallet?.totalBalance || '0'}</div>
              <div className="text-gray-400 text-sm mt-1">Available funds</div>
            </div>

            <div className="bg-gradient-to-br from-green-500/10 to-green-600/10 rounded-2xl p-6 border border-green-500/20">
              <div className="flex items-center justify-between mb-4">
                <FaMoneyBillWave className="text-green-400 text-xl" />
                <span className="text-green-400 text-sm font-medium">Available</span>
              </div>
              <div className="text-2xl font-bold text-white">${wallet?.availableBalance || '0'}</div>
              <div className="text-gray-400 text-sm mt-1">Withdrawable</div>
            </div>

            <div className="bg-gradient-to-br from-purple-500/10 to-purple-600/10 rounded-2xl p-6 border border-purple-500/20">
              <div className="flex items-center justify-between mb-4">
                <FaChartLine className="text-purple-400 text-xl" />
                <span className="text-purple-400 text-sm font-medium">Total Profit</span>
              </div>
              <div className="text-2xl font-bold text-white">${wallet?.totalProfit || '0'}</div>
              <div className="text-gray-400 text-sm mt-1">Earnings</div>
            </div>

            <div className="bg-gradient-to-br from-orange-500/10 to-orange-600/10 rounded-2xl p-6 border border-orange-500/20">
              <div className="flex items-center justify-between mb-4">
                <FaCoins className="text-orange-400 text-xl" />
                <span className="text-orange-400 text-sm font-medium">Total Deposits</span>
              </div>
              <div className="text-2xl font-bold text-white">${wallet?.totalDeposits || '0'}</div>
              <div className="text-gray-400 text-sm mt-1">All deposits</div>
            </div>

            <div className="bg-gradient-to-br from-red-500/10 to-red-600/10 rounded-2xl p-6 border border-red-500/20">
              <div className="flex items-center justify-between mb-4">
                <FaExchangeAlt className="text-red-400 text-xl" />
                <span className="text-red-400 text-sm font-medium">Total Withdrawals</span>
              </div>
              <div className="text-2xl font-bold text-white">${wallet?.totalWithdrawals || '0'}</div>
              <div className="text-gray-400 text-sm mt-1">All withdrawals</div>
            </div>
          </div>
        </div>

        {/* Transactions */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Deposits */}
          <div className="bg-gray-800/50 backdrop-blur-sm rounded-2xl p-6 border border-gray-700/50">
            <h3 className="text-lg font-bold text-white mb-4">Deposit History</h3>
            <div className="space-y-3">
              {deposits.slice(0, 5).map((deposit) => (
                <TransactionItem key={deposit._id} transaction={deposit} type="deposit" />
              ))}
              {deposits.length === 0 && (
                <div className="text-center text-gray-400 py-4">No deposits found</div>
              )}
            </div>
          </div>

          {/* Withdrawals */}
          <div className="bg-gray-800/50 backdrop-blur-sm rounded-2xl p-6 border border-gray-700/50">
            <h3 className="text-lg font-bold text-white mb-4">Withdrawal History</h3>
            <div className="space-y-3">
              {withdrawals.slice(0, 5).map((withdrawal) => (
                <TransactionItem key={withdrawal._id} transaction={withdrawal} type="withdrawal" />
              ))}
              {withdrawals.length === 0 && (
                <div className="text-center text-gray-400 py-4">No withdrawals found</div>
              )}
            </div>
          </div>
        </div>
      </main>

      {/* Balance Adjustment Modal */}
      <Modal
        isOpen={showAdjustModal}
        style={customStyles}
        onRequestClose={() => setShowAdjustModal(false)}
        contentLabel="Adjust Balance"
        ariaHideApp={false}
      >
        <div className="bg-gray-800 p-6 rounded-xl border border-gray-700 text-white">
          <h3 className="text-xl font-bold mb-4 bg-gradient-to-r from-blue-400 to-purple-400 bg-clip-text text-transparent">
            Adjust User Balance
          </h3>
          <form
            onSubmit={(e) => {
              e.preventDefault();
              handleBalanceUpdate({
                totalBalance,
                availableBalance,
                totalProfit,
                totalDeposits,
                totalWithdrawals,
                newKycStatus,
                KycAmount
              });
            }}
            className="space-y-4"
          >
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">Total Balance</label>
                <input
                  type="text"
                  className="w-full bg-gray-700 rounded-lg px-3 py-2 text-white border border-gray-600 focus:border-blue-500 focus:ring-1 focus:ring-blue-500 transition-colors"
                  value={totalBalance}
                  onChange={(e) => setTotalBalance(e.target.value)}
                  placeholder="e.g. 5000.00"
                />
                <div className="text-xs text-gray-400 mt-1">Current: ${wallet?.totalBalance || '0'}</div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">Available Balance</label>
                <input
                  type="text"
                  className="w-full bg-gray-700 rounded-lg px-3 py-2 text-white border border-gray-600 focus:border-blue-500 focus:ring-1 focus:ring-blue-500 transition-colors"
                  value={availableBalance}
                  onChange={(e) => setAvailableBalance(e.target.value)}
                  placeholder="e.g. 3000.00"
                />
                <div className="text-xs text-gray-400 mt-1">Current: ${wallet?.availableBalance || '0'}</div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">Total Profit</label>
                <input
                  type="text"
                  className="w-full bg-gray-700 rounded-lg px-3 py-2 text-white border border-gray-600 focus:border-blue-500 focus:ring-1 focus:ring-blue-500 transition-colors"
                  value={totalProfit}
                  onChange={(e) => setTotalProfit(e.target.value)}
                  placeholder="e.g. 200.00"
                />
                <div className="text-xs text-gray-400 mt-1">Current: ${wallet?.totalProfit || '0'}</div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">Total Deposits</label>
                <input
                  type="text"
                  className="w-full bg-gray-700 rounded-lg px-3 py-2 text-white border border-gray-600 focus:border-blue-500 focus:ring-1 focus:ring-blue-500 transition-colors"
                  value={totalDeposits}
                  onChange={(e) => setTotalDeposits(e.target.value)}
                  placeholder="e.g. 8000.00"
                />
                <div className="text-xs text-gray-400 mt-1">Current: ${wallet?.totalDeposits || '0'}</div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">Total Withdrawals</label>
                <input
                  type="text"
                  className="w-full bg-gray-700 rounded-lg px-3 py-2 text-white border border-gray-600 focus:border-blue-500 focus:ring-1 focus:ring-blue-500 transition-colors"
                  value={totalWithdrawals}
                  onChange={(e) => setTotalWithdrawals(e.target.value)}
                  placeholder="e.g. 1000.00"
                />
                <div className="text-xs text-gray-400 mt-1">Current: ${wallet?.totalWithdrawals || '0'}</div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">KYC Amount</label>
                <input
                  type="text"
                  className="w-full bg-gray-700 rounded-lg px-3 py-2 text-white border border-gray-600 focus:border-blue-500 focus:ring-1 focus:ring-blue-500 transition-colors"
                  value={KycAmount}
                  onChange={(e) => setKycAmount(e.target.value)}
                  placeholder="e.g. 1000.00"
                />
                <div className="text-xs text-gray-400 mt-1">Current: ${user?.kycAmount || '0'}</div>
              </div>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">KYC Status</label>
              <select
                value={newKycStatus}
                onChange={(e) => setKycNewStatus(e.target.value)}
                className="w-full bg-gray-700 rounded-lg px-3 py-2 text-white border border-gray-600 focus:border-blue-500 focus:ring-1 focus:ring-blue-500 transition-colors"
              >
                {Object.keys(statusConfig).map((status) => (
                  <option key={status} value={status} className="capitalize bg-gray-800">
                    {status}
                  </option>
                ))}
              </select>
              <div className="text-xs text-gray-400 mt-1">Current: {user?.kycStatus || 'pending'}</div>
            </div>

            <div className="flex justify-end space-x-3 pt-4">
              <button
                type="button"
                onClick={() => setShowAdjustModal(false)}
                className="px-4 py-2 bg-gray-700 hover:bg-gray-600 rounded-lg transition-colors duration-200 font-medium"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={isAdjustingBalance}
                className={`px-4 py-2 bg-gradient-to-r from-blue-500 to-purple-600 hover:from-blue-600 hover:to-purple-700 rounded-lg transition-all duration-200 font-medium flex items-center ${
                  isAdjustingBalance ? 'opacity-50 cursor-not-allowed' : ''
                }`}
              >
                {isAdjustingBalance ? (
                  <>
                    <FaClock className="animate-spin mr-2" /> Processing...
                  </>
                ) : (
                  'Update Balances'
                )}
              </button>
            </div>
          </form>
        </div>
      </Modal>

      {/* Password Update Modal */}
      <Modal
        isOpen={showPasswordModal}
        style={customStyles}
        onRequestClose={() => setShowPasswordModal(false)}
        contentLabel="Update Password"
        ariaHideApp={false}
      >
        <div className="bg-gray-800 p-6 rounded-xl border border-gray-700 text-white">
          <h3 className="text-xl font-bold mb-4 bg-gradient-to-r from-blue-400 to-purple-400 bg-clip-text text-transparent">
            Update User Password
          </h3>
          <form onSubmit={handlePasswordUpdate} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">New Password</label>
              <div className="relative">
                <input
                  type={showPassword ? "text" : "password"}
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  className="w-full bg-gray-700 rounded-lg px-3 py-2 text-white border border-gray-600 focus:border-blue-500 focus:ring-1 focus:ring-blue-500 transition-colors pr-10"
                  placeholder="Enter new password"
                  required
                  minLength={8}
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 text-gray-400 hover:text-gray-300"
                >
                  {showPassword ? <FaEyeSlash /> : <FaEye />}
                </button>
              </div>
              <div className="text-xs text-gray-400 mt-1">Minimum 8 characters</div>
            </div>

            <div className="flex justify-end space-x-3 pt-4">
              <button
                type="button"
                onClick={() => setShowPasswordModal(false)}
                className="px-4 py-2 bg-gray-700 hover:bg-gray-600 rounded-lg transition-colors duration-200 font-medium"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={isUpdatingPassword}
                className={`px-4 py-2 bg-gradient-to-r from-blue-500 to-purple-600 hover:from-blue-600 hover:to-purple-700 rounded-lg transition-all duration-200 font-medium flex items-center ${
                  isUpdatingPassword ? 'opacity-50 cursor-not-allowed' : ''
                }`}
              >
                {isUpdatingPassword ? (
                  <>
                    <FaClock className="animate-spin mr-2" /> Updating...
                  </>
                ) : (
                  'Update Password'
                )}
              </button>
            </div>
          </form>
        </div>
      </Modal>

      {/* Edit Profile Modal */}
      <Modal
        isOpen={showEditProfileModal}
        style={customStyles}
        onRequestClose={() => setShowEditProfileModal(false)}
        contentLabel="Edit Profile"
        ariaHideApp={false}
      >
        <div className="bg-gray-800 p-6 rounded-xl border border-gray-700 text-white">
          <h3 className="text-xl font-bold mb-4 bg-gradient-to-r from-blue-400 to-purple-400 bg-clip-text text-transparent">
            Edit User Profile
          </h3>
          <form onSubmit={handleProfileEdit}>
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">Name</label>
                <input 
                  type="text" 
                  name="name" 
                  defaultValue={user?.name || ''}
                  className="w-full bg-gray-700 rounded-lg px-3 py-2 text-white border border-gray-600 focus:border-blue-500 focus:ring-1 focus:ring-blue-500 transition-colors"
                  required 
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">Email</label>
                <input 
                  type="email" 
                  name="email" 
                  defaultValue={user?.email || ''}
                  className="w-full bg-gray-700 rounded-lg px-3 py-2 text-white border border-gray-600 focus:border-blue-500 focus:ring-1 focus:ring-blue-500 transition-colors"
                  required 
                  readOnly 
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">Phone</label>
                <input 
                  type="tel" 
                  name="phone" 
                  defaultValue={user?.phone || ''}
                  className="w-full bg-gray-700 rounded-lg px-3 py-2 text-white border border-gray-600 focus:border-blue-500 focus:ring-1 focus:ring-blue-500 transition-colors"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-300 mb-2">Currency</label>
                <input 
                  type="text" 
                  name="currency" 
                  defaultValue={user?.currency || ''}
                  className="w-full bg-gray-700 rounded-lg px-3 py-2 text-white border border-gray-600 focus:border-blue-500 focus:ring-1 focus:ring-blue-500 transition-colors"
                />
              </div>
            </div>

            <div className="flex justify-end space-x-3 mt-6">
              <button
                type="button"
                onClick={() => setShowEditProfileModal(false)}
                className="px-4 py-2 bg-gray-700 hover:bg-gray-600 rounded-lg transition-colors duration-200 font-medium"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={isEditingProfile}
                className={`px-4 py-2 bg-gradient-to-r from-blue-500 to-purple-600 hover:from-blue-600 hover:to-purple-700 rounded-lg transition-all duration-200 font-medium flex items-center ${
                  isEditingProfile ? 'opacity-50 cursor-not-allowed' : ''
                }`}
              >
                {isEditingProfile ? (
                  <>
                    <FaClock className="animate-spin mr-2" /> Updating...
                  </>
                ) : (
                  'Update Profile'
                )}
              </button>
            </div>
          </form>
        </div>
      </Modal>

      {/* Status Update Modal */}
      <StatusModal />

     <div style={{marginTop: '15%'}}>
       <Nav />
     </div>
    </div>
    
  );
}
